import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useNavigate } from 'react-router-dom';
import { UserCheck, Users, X, Sparkles, ChevronRight } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import ClaimProfileModal from './ClaimProfileModal';

const ClaimProfileBanner = () => {
  const { user, employeeProfile, claimableMatch, hasUnclaimedProfiles, claimProfile, isLoading } = useAuth();
  const navigate = useNavigate();
  const [dismissed, setDismissed] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [isClaiming, setIsClaiming] = useState(false);
  const [declinedMatch, setDeclinedMatch] = useState(false);

  if (isLoading || !user || dismissed || employeeProfile) return null;

  // Determine which state to render
  const showMatchSuggestion = claimableMatch && !declinedMatch;
  const showBrowsePrompt = !showMatchSuggestion && hasUnclaimedProfiles;
  const showCreatePrompt = !showMatchSuggestion && !showBrowsePrompt;

  const handleClaimMatch = async () => {
    if (!claimableMatch) return;
    setIsClaiming(true);
    await claimProfile(claimableMatch.id);
    setIsClaiming(false);
    navigate('/profile/edit', { state: { claimedProfile: claimableMatch } });
  };

  const handleModalClaimed = (profile: import('../lib/database.types').EmployeeRow) => {
    setShowModal(false);
    navigate('/profile/edit', { state: { claimedProfile: profile } });
  };

  // Prominent email-match suggestion
  if (showMatchSuggestion) {
    return (
      <>
        <div className="bg-primary-600/10 border-b border-primary-600/25">
          <div className="container-custom py-3 flex items-center gap-3">
            <div className="flex-shrink-0 w-7 h-7 rounded-full overflow-hidden border border-primary-500/40 bg-bolt-surfaceHover">
              {claimableMatch.avatar_url ? (
                <img src={claimableMatch.avatar_url} alt={claimableMatch.name} className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-[10px] font-semibold text-primary-300">
                  {claimableMatch.name.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase()}
                </div>
              )}
            </div>

            <p className="text-xs text-primary-300 flex-1 min-w-0">
              <span className="font-medium text-primary-200">Is this you?</span>
              {' '}We found a profile matching your email:{' '}
              <span className="font-semibold text-white">{claimableMatch.name}</span>
              {' '}({claimableMatch.role})
            </p>

            <div className="flex items-center gap-2 flex-shrink-0">
              <button
                onClick={handleClaimMatch}
                disabled={isClaiming}
                className="inline-flex items-center gap-1.5 text-xs font-medium bg-primary-600 hover:bg-primary-500 text-white px-3 py-1.5 rounded-lg transition-colors disabled:opacity-60"
              >
                <UserCheck className="h-3.5 w-3.5" />
                {isClaiming ? 'Linking...' : 'Yes, claim this profile'}
              </button>
              <button
                onClick={() => setDeclinedMatch(true)}
                className="text-xs text-primary-400/70 hover:text-primary-300 transition-colors px-2 py-1.5 rounded-lg hover:bg-primary-600/10"
              >
                Not me
              </button>
              <button
                onClick={() => setDismissed(true)}
                className="text-primary-400/60 hover:text-primary-300 transition-colors p-1"
                aria-label="Dismiss"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            </div>
          </div>
        </div>
        {showModal && (
          <ClaimProfileModal onClose={() => setShowModal(false)} onClaimed={handleModalClaimed} />
        )}
      </>
    );
  }

  // Softer prompt when unclaimed profiles exist but no email match
  if (showBrowsePrompt) {
    return (
      <>
        <div className="bg-bolt-surface border-b border-bolt-border">
          <div className="container-custom py-2.5 flex items-center gap-3">
            <Users className="h-3.5 w-3.5 text-bolt-textSubtle flex-shrink-0" />
            <p className="text-xs text-bolt-textMuted flex-1">
              Already on the team?{' '}
              <button
                onClick={() => setShowModal(true)}
                className="text-primary-400 hover:text-primary-300 font-medium transition-colors inline-flex items-center gap-0.5"
              >
                Link an existing profile
                <ChevronRight className="h-3 w-3" />
              </button>
              {' '}or{' '}
              <Link to="/profile/edit" className="text-primary-400 hover:text-primary-300 font-medium transition-colors">
                create a new one
              </Link>
            </p>
            <button
              onClick={() => setDismissed(true)}
              className="text-bolt-textSubtle hover:text-bolt-textMuted transition-colors flex-shrink-0"
              aria-label="Dismiss"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          </div>
        </div>
        {showModal && (
          <ClaimProfileModal onClose={() => setShowModal(false)} onClaimed={handleModalClaimed} />
        )}
      </>
    );
  }

  // Fallback: no unclaimed profiles — show original create-profile CTA
  if (showCreatePrompt) {
    return (
      <div className="bg-primary-600/10 border-b border-primary-600/25">
        <div className="container-custom py-2.5 flex items-center gap-3">
          <Sparkles className="h-3.5 w-3.5 text-primary-400 flex-shrink-0" />
          <p className="text-xs text-primary-300 flex-1">
            You&apos;re signed in but don&apos;t have a profile yet —{' '}
            <Link to="/profile/edit" className="underline hover:text-primary-200 font-medium">
              set it up now
            </Link>{' '}
            to appear on the team map.
          </p>
          <button
            onClick={() => setDismissed(true)}
            className="text-primary-400/60 hover:text-primary-300 transition-colors flex-shrink-0"
            aria-label="Dismiss"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>
    );
  }

  return null;
};

export default ClaimProfileBanner;
